import React, { useEffect, useRef } from 'react';
import * as THREE from 'three';

const HeroGlobe = () => {
  const mountRef = useRef(null);

  useEffect(() => {
    const mount = mountRef.current;
    if (!mount) return;

    // --- DETECT INITIAL THEME ---
    const isLightMode = document.documentElement.getAttribute('data-theme') === 'light';

    const getColors = (isLight) => isLight ? {
      core: 0x0055aa,    // Deep Blue
      shield: 0x1a1a1a,  // Dark Grey
      ring: 0x222222,    // Sharp Grey
      star: 0x888888,    // Medium Grey
      bg: 0xffffff       // White
    } : {
      core: 0x00F0FF,    // Cyan
      shield: 0x00FF41,  // Hacker Green
      ring: 0x333333,    // Dark Grey
      star: 0xffffff,    // White
      bg: 0x000000       // Black
    };

    let colors = getColors(isLightMode);

    // --- 1. SETUP SCENE ---
    const scene = new THREE.Scene();
    scene.fog = new THREE.FogExp2(colors.bg, 0.02);
    
    const camera = new THREE.PerspectiveCamera(25, mount.clientWidth / mount.clientHeight, 0.1, 1000);
    const isMobile = mount.clientWidth < 768;
    camera.position.z = isMobile ? 55 : 45; 

    const renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true });
    renderer.setSize(mount.clientWidth, mount.clientHeight);
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 1.5));
    mount.appendChild(renderer.domElement);

    const fortressGroup = new THREE.Group();
    scene.add(fortressGroup);

    if (!isMobile) {
        fortressGroup.position.x = 8; 
    } else {
        fortressGroup.position.y = 2;
    }

    // --- 3. MATERIALS (Stored in variables so we can update them later) ---
    const coreGeometry = new THREE.SphereGeometry(4, 64, 64); 
    const coreMaterial = new THREE.PointsMaterial({
      color: colors.core, 
      size: isLightMode ? 0.07 : 0.05,
      transparent: true,
      opacity: isLightMode ? 0.9 : 0.8,
    });
    const core = new THREE.Points(coreGeometry, coreMaterial);
    fortressGroup.add(core);

    const shieldGeometry = new THREE.IcosahedronGeometry(6, 1); 
    const shieldMaterial = new THREE.MeshBasicMaterial({
      color: colors.shield, 
      wireframe: true,
      transparent: true,
      opacity: isLightMode ? 0.6 : 0.15,
    });
    const shield = new THREE.Mesh(shieldGeometry, shieldMaterial);
    fortressGroup.add(shield);

    const ringGeo = new THREE.TorusGeometry(10, 0.05, 16, 100); 
    const ringMat = new THREE.MeshBasicMaterial({ 
        color: colors.ring, 
        transparent: true, 
        opacity: isLightMode ? 0.8 : 0.3 
    });
    
    const ring1 = new THREE.Mesh(ringGeo, ringMat);
    ring1.rotation.x = Math.PI / 2;
    ring1.rotation.y = Math.PI / 8;
    fortressGroup.add(ring1);

    const ring2 = new THREE.Mesh(ringGeo, ringMat);
    ring2.rotation.x = Math.PI / 2;
    ring2.rotation.y = -Math.PI / 8;
    fortressGroup.add(ring2);

    const particlesCount = 900; 
    const posArray = new Float32Array(particlesCount * 3);
    
    for(let i = 0; i < particlesCount * 3; i++) {
        posArray[i] = (Math.random() - 0.5) * 150; 
    }
    
    const particlesGeo = new THREE.BufferGeometry();
    particlesGeo.setAttribute('position', new THREE.BufferAttribute(posArray, 3));
    const particlesMat = new THREE.PointsMaterial({
        size: 0.1,
        color: colors.star,
        transparent: true,
        opacity: isLightMode ? 0.8 : 0.5,
        blending: isLightMode ? THREE.NormalBlending : THREE.AdditiveBlending
    });
    const starField = new THREE.Points(particlesGeo, particlesMat);
    scene.add(starField);

    // --- 4. THEME UPDATE FUNCTION (NO RELOADS) ---
    const updateThemeColors = (isLight) => {
      const newColors = getColors(isLight);
      
      // Update Fog
      scene.fog.color.setHex(newColors.bg);

      // Update Core
      coreMaterial.color.setHex(newColors.core);
      coreMaterial.size = isLight ? 0.07 : 0.05;
      coreMaterial.opacity = isLight ? 0.9 : 0.8;
      coreMaterial.needsUpdate = true;

      // Update Shield
      shieldMaterial.color.setHex(newColors.shield);
      shieldMaterial.opacity = isLight ? 0.6 : 0.15;
      shieldMaterial.needsUpdate = true;

      // Update Rings
      ringMat.color.setHex(newColors.ring);
      ringMat.opacity = isLight ? 0.8 : 0.3;
      ringMat.needsUpdate = true;

      // Update Stars
      particlesMat.color.setHex(newColors.star);
      particlesMat.opacity = isLight ? 0.8 : 0.5;
      particlesMat.blending = isLight ? THREE.NormalBlending : THREE.AdditiveBlending;
      particlesMat.needsUpdate = true;
    };

    // --- 5. THEME WATCHER ---
    const themeObserver = new MutationObserver((mutations) => {
        mutations.forEach((mutation) => {
            if (mutation.attributeName === "data-theme") {
                const isNowLight = document.documentElement.getAttribute('data-theme') === 'light';
                updateThemeColors(isNowLight); // <--- Instantly updates 3D colors instead of reloading
            }
        });
    });
    themeObserver.observe(document.documentElement, { attributes: true });

    // --- 6. INTERACTION & INTERSECTION OBSERVER ---
    let mouseX = 0;
    let mouseY = 0;
    
    const handleMouseMove = (event) => {
        mouseX = event.clientX / window.innerWidth - 0.5;
        mouseY = event.clientY / window.innerHeight - 0.5;
    };
    window.addEventListener('mousemove', handleMouseMove, { passive: true });

    let isVisible = true;
    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        isVisible = entry.isIntersecting;
      });
    }, { threshold: 0.01 }); 
    observer.observe(mount);

    // --- 7. ANIMATION LOOP ---
    let frameId;
    const animate = () => {
      frameId = requestAnimationFrame(animate);
      
      if (!isVisible) return;

      const time = Date.now() * 0.001;

      core.rotation.y += 0.002;
      shield.rotation.y -= 0.005;
      shield.rotation.x += 0.002;

      const scale = 1 + Math.sin(time * 2) * 0.02;
      shield.scale.set(scale, scale, scale);

      ring1.rotation.z += 0.002;
      ring2.rotation.z -= 0.002;

      starField.rotation.y = time * 0.02; 

      camera.position.x += (mouseX * 1.5 - camera.position.x) * 0.05;
      camera.position.y += (-mouseY * 1.5 - camera.position.y) * 0.05;
      camera.lookAt(scene.position);

      renderer.render(scene, camera);
    };

    animate();

    // --- RESIZE HANDLER ---
    const handleResize = () => {
      if (!mount) return;
      const width = mount.clientWidth;
      const height = mount.clientHeight;
      
      renderer.setSize(width, height);
      camera.aspect = width / height;
      
      const mobile = width < 768;
      camera.position.z = mobile ? 55 : 45;
      
      if (!mobile) {
        fortressGroup.position.x = 8;
        fortressGroup.position.y = 0;
      } else {
        fortressGroup.position.x = 0;
        fortressGroup.position.y = 2;
      }

      camera.updateProjectionMatrix();
    };

    window.addEventListener('resize', handleResize, { passive: true });

    // --- CLEANUP ---
    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('resize', handleResize);
      observer.disconnect();
      themeObserver.disconnect();
      cancelAnimationFrame(frameId);
      
      if (mount && renderer.domElement) {
        mount.removeChild(renderer.domElement);
      }
      
      coreGeometry.dispose(); coreMaterial.dispose();
      shieldGeometry.dispose(); shieldMaterial.dispose();
      ringGeo.dispose(); ringMat.dispose();
      particlesGeo.dispose(); particlesMat.dispose();
      renderer.dispose();
    };
  }, []);

  return (
    <div 
      ref={mountRef} 
      style={{ 
        width: '100%', 
        height: '100%', 
        position: 'absolute',
        top: 0,
        left: 0,
        zIndex: 0,
        pointerEvents: 'none',
        willChange: 'transform'
      }} 
    />
  );
};

export default HeroGlobe;